import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POLL_INTERVAL_MS = 2200;
const MAX_POLL_TRIES = 10;

const STEP_LABELS = [
  { step: 1, title: "계약서 작성",        description: "서비스 이용 계약 체결" },
  { step: 2, title: "자기소개서 작성",    description: "상세한 프로필 작성" },
  { step: 3, title: "혼인관계증명서 제출", description: "신원 확인 서류 제출" },
  { step: 4, title: "커플매니저 배정",    description: "전담 매니저 배정" },
  { step: 5, title: "줌 인터뷰",         description: "1:1 화상 인터뷰 진행" },
  { step: 6, title: "소개팅 진행",       description: "맞춤 매칭 시작" },
];

const ProcessSteps = ({ currentStep, history }) => {
  const cur = Math.max(0, Math.min(6, currentStep || 0));
  return (
    <div className="w-full" data-testid="process-steps">
      {/* Desktop horizontal */}
      <div className="hidden md:flex items-start justify-between gap-2 lg:gap-4">
        {STEP_LABELS.map((s, idx) => {
          const isDone = cur > s.step;
          const isCurrent = cur === s.step;
          return (
            <div key={s.step} className="flex-1 flex items-start gap-2 lg:gap-3">
              <div className="flex flex-col items-center flex-1" data-testid={`step-${s.step}`}>
                <div
                  className={`relative w-14 h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center font-bold text-lg lg:text-xl transition-all duration-500 shadow-lg ${
                    isDone
                      ? "bg-emerald-500 text-white shadow-emerald-200"
                      : isCurrent
                      ? "bg-burgundy-600 text-white ring-4 ring-burgundy-200 shadow-burgundy-200 scale-110"
                      : "bg-stone-800/90 text-white shadow-stone-200"
                  }`}
                >
                  {isDone ? <Check className="w-6 h-6" /> : s.step}
                  {isCurrent && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-amber-400 text-amber-900 text-[9px] font-bold rounded-full shadow">진행중</span>
                  )}
                </div>
                <div className="mt-3 text-center max-w-[140px]">
                  <div className={`text-sm lg:text-[15px] font-semibold ${isCurrent ? "text-burgundy-700" : "text-stone-800"}`}>{s.title}</div>
                  <div className="text-[11px] lg:text-xs text-stone-500 mt-0.5 leading-tight">{s.description}</div>
                </div>
              </div>
              {idx < STEP_LABELS.length - 1 && (
                <ArrowRight className={`w-5 h-5 mt-5 lg:mt-6 shrink-0 ${cur > s.step ? "text-emerald-500" : "text-burgundy-400/60"}`} />
              )}
            </div>
          );
        })}
      </div>
      {/* Mobile vertical */}
      <div className="md:hidden space-y-3">
        {STEP_LABELS.map((s) => {
          const isDone = cur > s.step;
          const isCurrent = cur === s.step;
          return (
            <div key={s.step} className="flex items-start gap-3" data-testid={`step-mobile-${s.step}`}>
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold shrink-0 ${
                  isDone ? "bg-emerald-500 text-white" : isCurrent ? "bg-burgundy-600 text-white ring-4 ring-burgundy-200" : "bg-stone-800/90 text-white"
                }`}
              >
                {isDone ? <Check className="w-5 h-5" /> : s.step}
              </div>
              <div className="flex-1 pt-1">
                <div className={`text-sm font-semibold ${isCurrent ? "text-burgundy-700" : "text-stone-800"}`}>
                  {s.title}
                  {isCurrent && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">진행중</span>}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">{s.description}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent history */}
      {history && history.length > 0 && (
        <div className="mt-10 pt-6 border-t border-stone-200">
          <h4 className="text-xs font-semibold tracking-wider text-stone-500 mb-3">진행 이력</h4>
          <ul className="space-y-1.5 text-xs text-stone-600">
            {[...history].reverse().slice(0, 5).map((h, i) => (
              <li key={i} className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-stone-400" />
                <span className="font-medium">[{h.step}단계]</span>
                <span>{(h.note || "갱신").slice(0, 80)}</span>
                <span className="text-stone-400">· {new Date(h.at).toLocaleString("ko-KR")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function DatingAgencyPaymentSuccessPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [inquiry, setInquiry] = useState(null);
  const [tries, setTries] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!token || !sessionId) return false;
    try {
      const res = await fetch(`${API_URL}/api/dating-agency/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      // Terminal errors → stop polling immediately
      if (res.status === 404 || res.status === 403) {
        setStatus("failed");
        return true;
      }
      if (!res.ok) {
        // 5xx — keep polling a few more times in case Stripe is temporarily down
        return false;
      }
      setInquiry(data.inquiry);
      if (data.payment_status === "paid") {
        setStatus("paid");
        return true;
      }
      if (["expired", "canceled", "failed"].includes(data.status)) {
        setStatus("failed");
        return true;
      }
      return false;
    } catch (e) {
      console.error("[paymentSuccess] poll error", e);
      return false;
    }
  }, [token, sessionId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !token) {
      toast.error("로그인이 필요합니다.");
      navigate("/dating-agency");
      return;
    }
    if (!sessionId) {
      toast.error("세션 정보가 없습니다.");
      navigate("/dating-agency");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      const done = await pollStatus();
      setTries((n) => n + 1);
      if (done || cancelled) return;
      if (tries >= MAX_POLL_TRIES) {
        setStatus("failed");
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, token, sessionId]);

  if (authLoading || status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 to-rose-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-burgundy-600 mx-auto mb-4" />
          <p className="text-stone-700 font-medium">결제 상태를 확인하고 있어요…</p>
          <p className="text-stone-400 text-xs mt-1">잠시만 기다려주세요 ({tries + 1}/{MAX_POLL_TRIES})</p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-lg text-center">
          <div className="text-red-500 text-5xl mb-3">⚠</div>
          <h2 className="text-xl font-bold text-stone-800 mb-2">결제가 완료되지 않았습니다</h2>
          <p className="text-stone-500 text-sm mb-6">결제가 취소되었거나 오류가 발생했어요. 다시 시도해주세요.</p>
          <Button onClick={() => navigate("/dating-agency")} className="bg-burgundy-600 hover:bg-burgundy-700 text-white">
            플랜 페이지로 돌아가기
          </Button>
        </div>
      </div>
    );
  }

  // status === "paid"
  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-rose-50/30 to-amber-50/40 pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500 text-white mb-4 shadow-lg shadow-emerald-200">
            <Check className="w-8 h-8" />
          </div>
          <p className="text-[11px] tracking-[0.3em] text-emerald-700 font-semibold mb-2">PAYMENT SUCCESSFUL</p>
          <h1 className="font-sans text-3xl md:text-4xl font-bold text-stone-800 mb-3">
            결제가 완료되었습니다 🎉
          </h1>
          <p className="text-stone-600 leading-relaxed">
            <span className="font-semibold text-burgundy-700">{inquiry?.plan_name}</span> 플랜이 시작되었어요.
            <br />
            지금부터 6단계 프로세스가 진행됩니다.
          </p>
          {inquiry?.plan_price ? (
            <p className="text-stone-400 text-xs mt-2">
              결제 금액 USD ${inquiry.plan_price.toLocaleString()} · 기간 {inquiry.plan_duration}
            </p>
          ) : null}
        </div>

        {/* 진행 순서 */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl border border-stone-200 shadow-xl p-6 md:p-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-2">진행 순서</h2>
            <p className="text-sm text-stone-500">체계적인 프로세스로 최적의 인연을 찾아드립니다</p>
          </div>
          <ProcessSteps currentStep={inquiry?.process_step || 1} history={inquiry?.process_history} />
        </div>

        {/* 다음 단계 안내 */}
        <div className="mt-8 bg-burgundy-50 border border-burgundy-100 rounded-2xl p-6 flex items-start gap-4">
          <Sparkles className="w-6 h-6 text-burgundy-600 shrink-0 mt-0.5" />
          <div className="text-sm text-stone-700 leading-relaxed">
            <p className="font-semibold text-burgundy-700 mb-1">다음 단계 안내</p>
            영업일 기준 24시간 이내에 담당 커플매니저가 <span className="font-semibold">계약서 작성을 위해 연락</span>을 드릴 예정입니다.
            궁금하신 점은 마이페이지에서 진행 상황을 확인하시거나 고객센터로 문의해주세요.
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mt-10">
          <Link to="/my">
            <Button variant="outline">마이페이지</Button>
          </Link>
          <Link to="/community">
            <Button className="bg-burgundy-600 hover:bg-burgundy-700 text-white">커뮤니티 둘러보기</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
